import React, { useEffect, useState, useMemo } from 'react';
import { getEstimatedCredits } from '@shared/billing/costEstimator';
import { SmartModelSelector } from './SmartModelSelector';
import { Volume2 } from 'lucide-react';
import { NODE_SELECT_CLASS, NODE_SELECT_COMPACT_CLASS, NODE_LABEL_CLASS, NODE_LABEL_COMPACT_CLASS, NODE_WARNING_CLASS, NODE_TIP_CLASS } from './nodeStyles';

/** Build a human-readable description of a hidden setting for the "More in Preset" hint */
function describeHiddenSetting(field: SettingField): string {
  if (field.type === 'select' && field.options?.length) {
    const count = field.options.length;
    const label = field.label.toLowerCase();
    if (label === 'style') return `${count} ${count >= 10 ? 'creative' : ''} style${count > 1 ? 's' : ''}`.replace('  ', ' ');
    return `${field.label} (${count} options)`;
  }
  if (field.type === 'slider') {
    const label = field.label.toLowerCase();
    if (label.includes('inference') || label.includes('steps')) return 'quality fine-tuning';
    if (label.includes('guidance')) return 'creativity control';
    return field.label.toLowerCase();
  }
  if (field.type === 'toggle') {
    return field.label.toLowerCase().replace('expand ', 'auto ').replace('enhance ', 'auto ');
  }
  return field.label.toLowerCase();
}

// Types matching the model registry
export interface SettingField {
  key: string;
  label: string;
  type: 'select' | 'slider' | 'toggle';
  options?: Array<{ value: string; label: string }>;
  min?: number;
  max?: number;
  step?: number;
  default: any;
}

export interface ImageSizeOption {
  value: string;
  label: string;
}

export interface ImageModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  supportsReferenceImages: boolean;
  maxReferenceImages?: number;
  requiresReferenceImage?: boolean;
  sizeOptions?: ImageSizeOption[];
  aspectRatioOptions?: string[];
  settings: SettingField[];
  defaultSize?: string;
  defaultAspectRatio?: string;
  supportsCustomDimensions?: boolean;
  costPerImage: number;
}

export interface VideoModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  supportsReferenceImages: boolean;
  maxReferenceImages: number;
  requiresReferenceImage: boolean;
  durationOptions: Array<{ value: string; label: string }>;
  resolutionOptions: Array<{ value: string; label: string }>;
  aspectRatioOptions: Array<{ value: string; label: string }>;
  defaultDuration: string;
  defaultResolution: string;
  defaultAspectRatio: string;
  supportsAudio: boolean;
  settings: SettingField[];
  costPerSecond: number;
  mediaType: 'video';
}

export interface TextModelDefinition {
  id: string;
  name: string;
  provider: string;
  description?: string;
  maxOutputTokens: number;
  supportsSystemPrompt: boolean;
  settings: SettingField[];
  costPer1kTokens: { input: number; output: number };
}

export interface GenerationSettingsData {
  mediaType: 'image' | 'video' | 'text';
  model: string;
  size?: string;
  aspectRatio?: string;
  customWidth?: number;
  customHeight?: number;
  duration?: string;
  resolution?: string;
  modelSettings?: Record<string, any>;
}

const DEFAULT_IMAGE_MODEL_ID = 'gemini-2.5-flash-image';
const DEFAULT_VIDEO_MODEL_ID = 'seedance-1.5-t2v';
const DEFAULT_TEXT_MODEL_ID = 'gemini-3-flash-preview';

function pickDefaultModel<T extends { id: string }>(models: T[], defaultId: string): T | undefined {
  return models.find((model) => model.id === defaultId) || models[0];
}

// Cache models globally so we don't refetch on every component render
let cachedImageModels: ImageModelDefinition[] | null = null;
let imageFetchPromise: Promise<ImageModelDefinition[]> | null = null;
let cachedVideoModels: VideoModelDefinition[] | null = null;
let videoFetchPromise: Promise<VideoModelDefinition[]> | null = null;
let cachedTextModels: TextModelDefinition[] | null = null;
let textFetchPromise: Promise<TextModelDefinition[]> | null = null;

export function getImageModelsCache(): ImageModelDefinition[] | null {
  if (!cachedImageModels && !imageFetchPromise) fetchImageModels();
  return cachedImageModels;
}

export function getVideoModelsCache(): VideoModelDefinition[] | null {
  if (!cachedVideoModels && !videoFetchPromise) fetchVideoModels();
  return cachedVideoModels;
}

export function getTextModelsCache(): TextModelDefinition[] | null {
  if (!cachedTextModels && !textFetchPromise) fetchTextModels();
  return cachedTextModels;
}

function fetchImageModels(): Promise<ImageModelDefinition[]> {
  if (cachedImageModels) return Promise.resolve(cachedImageModels);
  if (imageFetchPromise) return imageFetchPromise;
  imageFetchPromise = fetch('/api/canvases/image-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedImageModels = data.data || [];
      return cachedImageModels!;
    })
    .catch(() => {
      imageFetchPromise = null;
      return [];
    });
  return imageFetchPromise;
}

function fetchVideoModels(): Promise<VideoModelDefinition[]> {
  if (cachedVideoModels) return Promise.resolve(cachedVideoModels);
  if (videoFetchPromise) return videoFetchPromise;
  videoFetchPromise = fetch('/api/canvases/video-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedVideoModels = data.data || [];
      return cachedVideoModels!;
    })
    .catch(() => {
      videoFetchPromise = null;
      return [];
    });
  return videoFetchPromise;
}

function fetchTextModels(): Promise<TextModelDefinition[]> {
  if (cachedTextModels) return Promise.resolve(cachedTextModels);
  if (textFetchPromise) return textFetchPromise;
  textFetchPromise = fetch('/api/canvases/text-models', { credentials: 'include' })
    .then((res) => res.json())
    .then((data) => {
      cachedTextModels = data.data || [];
      return cachedTextModels!;
    })
    .catch(() => {
      textFetchPromise = null;
      return [];
    });
  return textFetchPromise;
}

// Hook to load and access models
export function useModels() {
  const [imageModels, setImageModels] = useState<ImageModelDefinition[]>(cachedImageModels || []);
  const [videoModels, setVideoModels] = useState<VideoModelDefinition[]>(cachedVideoModels || []);
  const [textModels, setTextModels] = useState<TextModelDefinition[]>(cachedTextModels || []);

  useEffect(() => {
    fetchImageModels().then(setImageModels);
    fetchVideoModels().then(setVideoModels);
    fetchTextModels().then(setTextModels);
  }, []);

  return { imageModels, videoModels, textModels };
}

// Get default settings for a model
export function getDefaultSettingsForModel(
  model: ImageModelDefinition | VideoModelDefinition | TextModelDefinition | undefined,
  mediaType: 'image' | 'video' | 'text',
): GenerationSettingsData {
  if (!model) {
    return {
      mediaType,
      model: '',
      aspectRatio: mediaType === 'text' ? undefined : '1:1',
    };
  }

  const modelSettings: Record<string, any> = {};
  for (const field of model.settings) {
    modelSettings[field.key] = field.default;
  }

  if (mediaType === 'video') {
    const videoModel = model as VideoModelDefinition;
    return {
      mediaType: 'video',
      model: videoModel.id,
      duration: videoModel.defaultDuration || '5',
      resolution: videoModel.defaultResolution || '720p',
      aspectRatio: videoModel.defaultAspectRatio || '16:9',
      modelSettings,
    };
  } else if (mediaType === 'text') {
    const textModel = model as TextModelDefinition;
    return {
      mediaType: 'text',
      model: textModel.id,
      modelSettings,
    };
  } else {
    const imageModel = model as ImageModelDefinition;
    return {
      mediaType: 'image',
      model: imageModel.id,
      size: imageModel.defaultSize || '1K',
      aspectRatio: imageModel.defaultAspectRatio || '1:1',
      modelSettings,
    };
  }
}

// Cost display component — shows customer-facing credit pricing from the shared estimator.
function CreditDisplay({ settings, compact }: { settings: GenerationSettingsData; compact?: boolean }) {
  const costCredits = useMemo(() => getEstimatedCredits({
    modelId: settings.model,
    mediaType: settings.mediaType,
    size: settings.size,
    duration: settings.duration,
    resolution: settings.resolution,
    audioEnabled: settings.modelSettings?.generate_audio !== false,
    modelSettings: settings.modelSettings,
  }), [settings.model, settings.mediaType, settings.size, settings.duration, settings.resolution, settings.modelSettings]);

  if (!settings.model) return null;

  const isText = settings.mediaType === 'text';
  const label = isText ? 'Free' : `${costCredits.toLocaleString()} credits`;
  const colorClass = isText
    ? 'text-green-600 bg-green-50'
    : costCredits <= 50
      ? 'text-blue-600 bg-blue-50'
      : costCredits <= 250
        ? 'text-warning-600 bg-warning-50'
        : 'text-red-600 bg-red-50';

  return (
    <div className={`${compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} ${colorClass} rounded font-medium inline-block`}>
      {label}
    </div>
  );
}

interface GenerationSettingsProps {
  settings: GenerationSettingsData;
  onChange: (settings: GenerationSettingsData) => void;
  compact?: boolean; // For inline mode in GenerateNode
  showMediaTypeToggle?: boolean;
  className?: string;
  connectedRefImageCount?: number; // For context-aware model selector
}

export function GenerationSettings({
  settings,
  onChange,
  compact = false,
  showMediaTypeToggle = true,
  className = '',
  connectedRefImageCount,
}: GenerationSettingsProps) {
  const { imageModels, videoModels, textModels } = useModels();
  const mediaType = settings.mediaType || 'image';

  const activeModels =
    mediaType === 'video' ? videoModels : mediaType === 'text' ? textModels : imageModels;
  const isLoading = activeModels.length === 0;

  const selectedModel =
    mediaType === 'video'
      ? videoModels.find((m) => m.id === settings.model) || videoModels[0]
      : mediaType === 'text'
        ? textModels.find((m) => m.id === settings.model) || textModels[0]
        : imageModels.find((m) => m.id === settings.model) || imageModels[0];

  const handleMediaTypeChange = (newMediaType: 'image' | 'video' | 'text') => {
    if (newMediaType === 'video') {
      const firstModel = pickDefaultModel(cachedVideoModels || videoModels, DEFAULT_VIDEO_MODEL_ID);
      onChange(getDefaultSettingsForModel(firstModel, 'video'));
    } else if (newMediaType === 'text') {
      const firstModel = pickDefaultModel(cachedTextModels || textModels, DEFAULT_TEXT_MODEL_ID);
      onChange(getDefaultSettingsForModel(firstModel, 'text'));
    } else {
      const firstModel = pickDefaultModel(cachedImageModels || imageModels, DEFAULT_IMAGE_MODEL_ID);
      onChange(getDefaultSettingsForModel(firstModel, 'image'));
    }
  };

  const handleModelChange = (modelId: string) => {
    if (mediaType === 'video') {
      const newModel = videoModels.find((m) => m.id === modelId);
      if (newModel) {
        onChange(getDefaultSettingsForModel(newModel, 'video'));
      }
    } else if (mediaType === 'text') {
      const newModel = textModels.find((m) => m.id === modelId);
      if (newModel) {
        onChange(getDefaultSettingsForModel(newModel, 'text'));
      }
    } else {
      const newModel = imageModels.find((m) => m.id === modelId);
      if (newModel) {
        onChange(getDefaultSettingsForModel(newModel, 'image'));
      }
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (mediaType === 'video') {
      if (!videoModels.length) return;
      const hasModel = videoModels.some((model) => model.id === settings.model);
      if (!hasModel) {
        const defaultModel = pickDefaultModel(videoModels, DEFAULT_VIDEO_MODEL_ID);
        if (defaultModel) onChange(getDefaultSettingsForModel(defaultModel, 'video'));
      }
      return;
    }

    if (mediaType === 'text') {
      if (!textModels.length) return;
      const hasModel = textModels.some((model) => model.id === settings.model);
      if (!hasModel) {
        const defaultModel = pickDefaultModel(textModels, DEFAULT_TEXT_MODEL_ID);
        if (defaultModel) onChange(getDefaultSettingsForModel(defaultModel, 'text'));
      }
      return;
    }

    if (!imageModels.length) return;
    const hasModel = imageModels.some((model) => model.id === settings.model);
    if (!hasModel) {
      const defaultModel = pickDefaultModel(imageModels, DEFAULT_IMAGE_MODEL_ID);
      if (defaultModel) onChange(getDefaultSettingsForModel(defaultModel, 'image'));
    }
  }, [isLoading, mediaType, imageModels, videoModels, textModels, settings.model, onChange]);

  const handleFieldChange = (field: string, value: any) => {
    onChange({ ...settings, [field]: value });
  };

  const handleSettingChange = (key: string, value: any) => {
    onChange({
      ...settings,
      modelSettings: { ...settings.modelSettings, [key]: value },
    });
  };

  const selectClass = compact ? NODE_SELECT_COMPACT_CLASS : NODE_SELECT_CLASS;
  const labelClass = compact ? NODE_LABEL_COMPACT_CLASS : NODE_LABEL_CLASS;

  function renderSettingField(field: SettingField) {
    const value = settings.modelSettings?.[field.key] ?? field.default;
    switch (field.type) {
      case 'select':
        return (
          <select
            className={selectClass}
            value={value}
            onChange={(e) => handleSettingChange(field.key, e.target.value)}
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'slider':
        return (
          <div className="flex items-center gap-2">
            <input
              type="range"
              className="flex-1"
              min={field.min}
              max={field.max}
              step={field.step}
              value={value}
              onChange={(e) => handleSettingChange(field.key, Number(e.target.value))}
            />
            <span
              className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-500 min-w-[2rem] text-right font-mono`}
            >
              {value}
            </span>
          </div>
        );
      case 'toggle':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleSettingChange(field.key, e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
              {value ? 'On' : 'Off'}
            </span>
          </label>
        );
      default:
        return null;
    }
  }

  if (isLoading) {
    return (
      <div className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-400 ${className}`}>
        Loading models...
      </div>
    );
  }

  const selectedImageModel = selectedModel as ImageModelDefinition;
  const selectedVideoModel = selectedModel as VideoModelDefinition;
  const selectedTextModel = selectedModel as TextModelDefinition;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Media Type Toggle */}
      {showMediaTypeToggle && (
        <div>
          <label className={labelClass}>Type</label>
          <div className="flex rounded-md overflow-hidden border border-gray-200">
            <button
              type="button"
              className={`flex-1 ${compact ? 'text-xs py-1 px-2' : 'text-xs py-1 px-2'} ${mediaType === 'image' ? 'bg-primary-100 text-primary-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('image')}
            >
              Image
            </button>
            <button
              type="button"
              className={`flex-1 ${compact ? 'text-xs py-1 px-2' : 'text-xs py-1 px-2'} ${mediaType === 'video' ? 'bg-purple-100 text-purple-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('video')}
            >
              Video
            </button>
            <button
              type="button"
              className={`flex-1 ${compact ? 'text-xs py-1 px-2' : 'text-xs py-1 px-2'} ${mediaType === 'text' ? 'bg-teal-100 text-teal-700 font-semibold' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
              onClick={() => handleMediaTypeChange('text')}
            >
              Text
            </button>
          </div>
        </div>
      )}

      {/* Model selector */}
      <div>
        <label className={labelClass}>Model</label>
        <SmartModelSelector
          mediaType={mediaType}
          selectedModelId={settings.model || activeModels[0]?.id || ''}
          onSelect={handleModelChange}
          models={activeModels}
          connectedRefImageCount={connectedRefImageCount}
          compact={compact}
        />
      </div>

      {/* Credit cost display — only in full mode; compact mode shows credits in SmartModelSelector */}
      {!compact && <CreditDisplay settings={settings} compact={compact} />}

      {/* IMAGE-specific settings */}
      {mediaType === 'image' && selectedImageModel && (
        <>
          {/* Size */}
          {selectedImageModel.sizeOptions && (
            <div>
              <label className={labelClass}>Size</label>
              <select
                className={selectClass}
                value={settings.size || selectedImageModel.defaultSize || ''}
                onChange={(e) => handleFieldChange('size', e.target.value)}
              >
                {selectedImageModel.sizeOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Aspect Ratio */}
          {selectedImageModel.aspectRatioOptions && (
            <div>
              <label className={labelClass}>Aspect Ratio</label>
              <select
                className={selectClass}
                value={settings.aspectRatio || selectedImageModel.defaultAspectRatio || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val !== 'custom') {
                    onChange({
                      ...settings,
                      aspectRatio: val,
                      customWidth: undefined,
                      customHeight: undefined,
                    });
                  } else {
                    handleFieldChange('aspectRatio', val);
                  }
                }}
              >
                {selectedImageModel.aspectRatioOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
                {selectedImageModel.supportsCustomDimensions && (
                  <option value="custom">Custom</option>
                )}
              </select>
            </div>
          )}

          {/* Custom Dimensions (when Custom aspect ratio is selected) */}
          {selectedImageModel.supportsCustomDimensions && settings.aspectRatio === 'custom' && (
            <div>
              <label className={labelClass}>Dimensions (px)</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  className={selectClass}
                  placeholder="Width"
                  min={256}
                  max={4096}
                  value={settings.customWidth || ''}
                  onChange={(e) =>
                    handleFieldChange(
                      'customWidth',
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
                <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-400`}>x</span>
                <input
                  type="number"
                  className={selectClass}
                  placeholder="Height"
                  min={256}
                  max={4096}
                  value={settings.customHeight || ''}
                  onChange={(e) =>
                    handleFieldChange(
                      'customHeight',
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                />
              </div>
            </div>
          )}

          {/* Model-specific settings */}
          {!compact &&
            selectedImageModel.settings.map((field) => (
              <div key={field.key}>
                <label className={labelClass}>{field.label}</label>
                {renderSettingField(field)}
              </div>
            ))}

          {/* Compact hint — hidden settings available in Preset */}
          {compact && selectedImageModel.settings.length > 0 && (() => {
            const descs: string[] = [];
            const hasSteps = selectedImageModel.settings.some((f) => f.label.toLowerCase().includes('inference') || f.label.toLowerCase().includes('steps'));
            const hasGuidance = selectedImageModel.settings.some((f) => f.label.toLowerCase().includes('guidance'));
            if (hasSteps && hasGuidance) {
              descs.push('quality & creativity controls');
            } else {
              if (hasSteps) descs.push('quality fine-tuning');
              if (hasGuidance) descs.push('creativity control');
            }
            for (const f of selectedImageModel.settings) {
              if (f.label.toLowerCase().includes('inference') || f.label.toLowerCase().includes('steps') || f.label.toLowerCase().includes('guidance')) continue;
              descs.push(describeHiddenSetting(f));
            }
            return (
              <div className={NODE_TIP_CLASS}>
                <span className="text-gray-500">Use a Preset node for:</span>{' '}
                {descs.join(', ')}
              </div>
            );
          })()}

          {/* Reference image info */}
          {!compact && selectedImageModel.requiresReferenceImage && (
            <div
              className={NODE_WARNING_CLASS}
            >
              Requires reference image(s)
            </div>
          )}
        </>
      )}

      {/* VIDEO-specific settings */}
      {mediaType === 'video' && selectedVideoModel && (
        <>
          {/* Duration */}
          {selectedVideoModel.durationOptions?.length > 0 && (
            <div>
              <label className={labelClass}>Duration</label>
              <select
                className={selectClass}
                value={settings.duration || selectedVideoModel.defaultDuration}
                onChange={(e) => handleFieldChange('duration', e.target.value)}
              >
                {selectedVideoModel.durationOptions.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Resolution */}
          {!compact && selectedVideoModel.resolutionOptions?.length > 0 && (
            <div>
              <label className={labelClass}>Resolution</label>
              <select
                className={selectClass}
                value={settings.resolution || selectedVideoModel.defaultResolution}
                onChange={(e) => handleFieldChange('resolution', e.target.value)}
              >
                {selectedVideoModel.resolutionOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Aspect Ratio */}
          {selectedVideoModel.aspectRatioOptions?.length > 0 && (
            <div>
              <label className={labelClass}>Aspect Ratio</label>
              <select
                className={selectClass}
                value={settings.aspectRatio || selectedVideoModel.defaultAspectRatio}
                onChange={(e) => handleFieldChange('aspectRatio', e.target.value)}
              >
                {selectedVideoModel.aspectRatioOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Model-specific settings */}
          {!compact &&
            selectedVideoModel.settings?.map((field) => (
              <div key={field.key}>
                <label className={labelClass}>{field.label}</label>
                {renderSettingField(field)}
              </div>
            ))}

          {/* Compact hint — hidden settings available in Preset */}
          {compact && (() => {
            const descs: string[] = [];
            if (selectedVideoModel.resolutionOptions?.length) {
              descs.push(`${selectedVideoModel.resolutionOptions.length} resolution options`);
            }
            if (selectedVideoModel.settings?.length) {
              const hasSteps = selectedVideoModel.settings.some((f) => f.label.toLowerCase().includes('inference') || f.label.toLowerCase().includes('steps'));
              const hasGuidance = selectedVideoModel.settings.some((f) => f.label.toLowerCase().includes('guidance'));
              if (hasSteps && hasGuidance) {
                descs.push('quality & creativity controls');
              } else {
                if (hasSteps) descs.push('quality fine-tuning');
                if (hasGuidance) descs.push('creativity control');
              }
              for (const f of selectedVideoModel.settings) {
                if (f.label.toLowerCase().includes('inference') || f.label.toLowerCase().includes('steps') || f.label.toLowerCase().includes('guidance')) continue;
                descs.push(describeHiddenSetting(f));
              }
            }
            return descs.length > 0 ? (
              <div className={NODE_TIP_CLASS}>
                <span className="text-gray-500">Use a Preset node for:</span>{' '}
                {descs.join(', ')}
              </div>
            ) : null;
          })()}

          {/* Audio indicator */}
          {!compact && selectedVideoModel.supportsAudio && (
            <div
              className={`${compact ? 'text-[10px]' : 'text-xs'} text-green-600 bg-green-50 p-1.5 rounded flex items-center gap-1`}
            >
              <Volume2 className="w-3 h-3" />
              Generates audio
            </div>
          )}
        </>
      )}

      {/* TEXT-specific settings */}
      {mediaType === 'text' && selectedTextModel && (
        <>
          {/* Model-specific settings (temperature, max length) */}
          {selectedTextModel.settings?.map((field) => (
            <div key={field.key}>
              <label className={labelClass}>{field.label}</label>
              {renderSettingField(field)}
            </div>
          ))}

          {/* Description */}
          {selectedTextModel.description && (
            <div className={`${compact ? 'text-[10px]' : 'text-xs'} text-gray-400`}>
              {selectedTextModel.description}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Utility to get a summary string for display
export function getSettingsSummary(
  settings: GenerationSettingsData,
  imageModels: ImageModelDefinition[],
  videoModels: VideoModelDefinition[],
  textModels?: TextModelDefinition[],
): string {
  const models =
    settings.mediaType === 'video'
      ? videoModels
      : settings.mediaType === 'text'
        ? textModels || []
        : imageModels;
  const model = models.find((m) => m.id === settings.model);
  const modelName = model?.name || 'Unknown';

  if (settings.mediaType === 'text') {
    return `${modelName}`;
  }

  const ratio = settings.aspectRatio || '1:1';
  return `${modelName} · ${ratio}`;
}
